import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { CreditCard, DollarSign, Settings, Eye, EyeOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ stripe: false, paypal: false });
  
  // Stripe form
  const [stripeForm, setStripeForm] = useState({
    stripe_api_key: '',
    stripe_webhook_secret: '',
    stripe_enabled: true
  });
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showStripeWebhook, setShowStripeWebhook] = useState(false);
  
  // PayPal form
  const [paypalForm, setPaypalForm] = useState({
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_mode: 'sandbox',
    paypal_enabled: true
  });
  const [showPaypalId, setShowPaypalId] = useState(false);
  const [showPaypalSecret, setShowPaypalSecret] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/settings/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      
      // Update forms with current enabled status
      setStripeForm(prev => ({ ...prev, stripe_enabled: response.data.stripe_enabled }));
      setPaypalForm(prev => ({ 
        ...prev, 
        paypal_enabled: response.data.paypal_enabled,
        paypal_mode: response.data.paypal_mode
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé. Seuls les super administrateurs peuvent gérer les paramètres.');
      } else {
        toast.error('Erreur lors du chargement des paramètres');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStripe = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      
      if (stripeForm.stripe_api_key) formData.append('stripe_api_key', stripeForm.stripe_api_key);
      if (stripeForm.stripe_webhook_secret) formData.append('stripe_webhook_secret', stripeForm.stripe_webhook_secret);
      formData.append('stripe_enabled', stripeForm.stripe_enabled);

      await axios.post(`${API}/admin/settings/payment-gateways/stripe`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Paramètres Stripe mis à jour');
      
      // Clear password fields
      setStripeForm({
        stripe_api_key: '',
        stripe_webhook_secret: '',
        stripe_enabled: stripeForm.stripe_enabled
      });
      
      fetchSettings();
    } catch (error) {
      console.error('Error updating Stripe settings:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePaypal = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      
      if (paypalForm.paypal_client_id) formData.append('paypal_client_id', paypalForm.paypal_client_id);
      if (paypalForm.paypal_client_secret) formData.append('paypal_client_secret', paypalForm.paypal_client_secret);
      formData.append('paypal_mode', paypalForm.paypal_mode);
      formData.append('paypal_enabled', paypalForm.paypal_enabled);

      await axios.post(`${API}/admin/settings/payment-gateways/paypal`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Paramètres PayPal mis à jour');
      
      // Clear password fields
      setPaypalForm({
        paypal_client_id: '',
        paypal_client_secret: '',
        paypal_mode: paypalForm.paypal_mode,
        paypal_enabled: paypalForm.paypal_enabled
      });
      
      fetchSettings();
    } catch (error) {
      console.error('Error updating PayPal settings:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (gateway) => {
    try {
      setTesting({ ...testing, [gateway]: true });
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('gateway', gateway);

      const response = await axios.post(
        `${API}/admin/settings/payment-gateways/test-connection`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(`Error testing ${gateway}:`, error);
      toast.error(error.response?.data?.detail || `Erreur lors du test de connexion ${gateway}`);
    } finally {
      setTesting({ ...testing, [gateway]: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Paramètres de Paiement</h1>
        <p className="text-slate-600">Configurez vos passerelles de paiement Stripe et PayPal</p>
      </div>

      {/* Stripe Settings */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-purple-900 to-purple-800 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-6 w-6" />
              <CardTitle className="text-xl">Stripe</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={settings?.stripe_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {settings?.stripe_enabled ? 'Activé' : 'Désactivé'}
              </Badge>
              {settings?.stripe_api_key_set && (
                <Badge className="bg-blue-100 text-blue-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Configuré
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleUpdateStripe} className="space-y-4">
            {/* Stripe API Key */}
            <div>
              <Label htmlFor="stripe_api_key" className="flex items-center justify-between">
                <span>Clé API Stripe</span>
                {settings?.stripe_api_key_set && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Clé déjà configurée
                  </span>
                )}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="stripe_api_key"
                  type={showStripeKey ? 'text' : 'password'}
                  value={stripeForm.stripe_api_key}
                  onChange={(e) => setStripeForm({ ...stripeForm, stripe_api_key: e.target.value })}
                  placeholder={settings?.stripe_api_key_set ? 'Laisser vide pour conserver' : 'sk_test_...'}
                />
                <button
                  type="button"
                  onClick={() => setShowStripeKey(!showStripeKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showStripeKey ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </button>
              </div>
            </div>

            {/* Stripe Webhook Secret */}
            <div>
              <Label htmlFor="stripe_webhook_secret" className="flex items-center justify-between">
                <span>Webhook Secret (optionnel)</span>
                {settings?.stripe_webhook_secret_set && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Secret configuré
                  </span>
                )}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="stripe_webhook_secret"
                  type={showStripeWebhook ? 'text' : 'password'}
                  value={stripeForm.stripe_webhook_secret}
                  onChange={(e) => setStripeForm({ ...stripeForm, stripe_webhook_secret: e.target.value })}
                  placeholder={settings?.stripe_webhook_secret_set ? 'Laisser vide pour conserver' : 'whsec_...'}
                />
                <button
                  type="button"
                  onClick={() => setShowStripeWebhook(!showStripeWebhook)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showStripeWebhook ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </button>
              </div>
            </div>

            {/* Stripe Enabled */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="stripe_enabled"
                checked={stripeForm.stripe_enabled}
                onChange={(e) => setStripeForm({ ...stripeForm, stripe_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
              />
              <Label htmlFor="stripe_enabled">Activer les paiements Stripe</Label>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="bg-purple-900 hover:bg-purple-800">
                <Settings className="h-4 w-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              {settings?.stripe_api_key_set && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testConnection('stripe')}
                  disabled={testing.stripe}
                >
                  {testing.stripe ? 'Test en cours...' : 'Tester la connexion'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* PayPal Settings */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="h-6 w-6" />
              <CardTitle className="text-xl">PayPal</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={settings?.paypal_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {settings?.paypal_enabled ? 'Activé' : 'Désactivé'}
              </Badge>
              {settings?.paypal_client_id_set && (
                <Badge className="bg-blue-100 text-blue-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Configuré
                </Badge>
              )}
              <Badge className="bg-slate-100 text-slate-800">
                Mode: {settings?.paypal_mode}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleUpdatePaypal} className="space-y-4">
            {/* PayPal Client ID */}
            <div>
              <Label htmlFor="paypal_client_id" className="flex items-center justify-between">
                <span>Client ID PayPal</span>
                {settings?.paypal_client_id_set && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    ID déjà configuré
                  </span>
                )}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="paypal_client_id"
                  type={showPaypalId ? 'text' : 'password'}
                  value={paypalForm.paypal_client_id}
                  onChange={(e) => setPaypalForm({ ...paypalForm, paypal_client_id: e.target.value })}
                  placeholder={settings?.paypal_client_id_set ? 'Laisser vide pour conserver' : 'AYS...'}
                />
                <button
                  type="button"
                  onClick={() => setShowPaypalId(!showPaypalId)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPaypalId ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </button>
              </div>
            </div>

            {/* PayPal Client Secret */}
            <div>
              <Label htmlFor="paypal_client_secret" className="flex items-center justify-between">
                <span>Client Secret PayPal</span>
                {settings?.paypal_client_secret_set && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Secret configuré
                  </span>
                )}
              </Label>
              <div className="relative mt-2">
                <Input
                  id="paypal_client_secret"
                  type={showPaypalSecret ? 'text' : 'password'}
                  value={paypalForm.paypal_client_secret}
                  onChange={(e) => setPaypalForm({ ...paypalForm, paypal_client_secret: e.target.value })}
                  placeholder={settings?.paypal_client_secret_set ? 'Laisser vide pour conserver' : 'EJN...'}
                />
                <button
                  type="button"
                  onClick={() => setShowPaypalSecret(!showPaypalSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPaypalSecret ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </button>
              </div>
            </div>

            {/* PayPal Mode */}
            <div>
              <Label htmlFor="paypal_mode">Mode PayPal</Label>
              <select
                id="paypal_mode"
                value={paypalForm.paypal_mode}
                onChange={(e) => setPaypalForm({ ...paypalForm, paypal_mode: e.target.value })}
                className="w-full px-3 py-2 mt-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="sandbox">Sandbox (Test)</option>
                <option value="live">Live (Production)</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {paypalForm.paypal_mode === 'sandbox' ? 
                  '⚠️ Mode test - utilisez des identifiants sandbox' : 
                  '🔴 Mode production - utilisez des identifiants live'}
              </p>
            </div>

            {/* PayPal Enabled */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="paypal_enabled"
                checked={paypalForm.paypal_enabled}
                onChange={(e) => setPaypalForm({ ...paypalForm, paypal_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
              />
              <Label htmlFor="paypal_enabled">Activer les paiements PayPal</Label>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800">
                <Settings className="h-4 w-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              {settings?.paypal_client_id_set && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testConnection('paypal')}
                  disabled={testing.paypal}
                >
                  {testing.paypal ? 'Test en cours...' : 'Tester la connexion'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-2">Notes importantes :</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Les clés API sont stockées de manière sécurisée dans la base de données</li>
                <li>Laissez les champs vides pour conserver les valeurs existantes</li>
                <li>Utilisez le mode Sandbox pour les tests et Live pour la production</li>
                <li>Testez la connexion après chaque modification</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSettings;
